/**
 * @fileoverview Resizable Component
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * @based-on shadcn/ui + Figma design specification
 */

import { createContext, useContext, useState, useRef, useCallback, Children, isValidElement, cloneElement, ReactElement, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { GripVertical } from 'lucide-react';

export type ResizableDirection = 'horizontal' | 'vertical';

export interface ResizablePanelGroupProps {
  direction?: ResizableDirection;
  children: ReactNode;
  className?: string;
}

export interface ResizablePanelProps {
  defaultSize?: number;
  minSize?: number;
  maxSize?: number;
  children: ReactNode;
  className?: string;
}

export interface ResizableHandleProps {
  className?: string;
}

interface ResizableContextValue {
  direction: ResizableDirection;
  sizes: number[];
  onResize: (index: number, delta: number) => void;
}

interface InternalResizablePanelProps extends ResizablePanelProps {
  _index?: number;
}

const ResizableContext = createContext<ResizableContextValue | null>(null);

export function ResizablePanelGroup({ direction = 'horizontal', children, className }: ResizablePanelGroupProps) {
  const childArray = Children.toArray(children).filter(isValidElement);
  const panelCount = childArray.filter((child) => isValidElement(child) && child.type === ResizablePanel).length;

  const [sizes, setSizes] = useState<number[]>(() =>
    Array(panelCount).fill(100 / panelCount)
  );

  const onResize = useCallback((index: number, delta: number) => {
    setSizes((prevSizes) => {
      const newSizes = [...prevSizes];
      const currentSize = newSizes[index];
      const nextSize = newSizes[index + 1];

      const newCurrentSize = Math.max(10, Math.min(90, currentSize + delta));
      const diff = newCurrentSize - currentSize;

      newSizes[index] = newCurrentSize;
      newSizes[index + 1] = nextSize - diff;

      return newSizes;
    });
  }, []);

  let panelIndex = 0;
  const childrenWithProps = childArray.map((child) => {
    if (isValidElement(child) && child.type === ResizablePanel) {
      const currentIndex = panelIndex;
      panelIndex++;
      const panelChild = child as ReactElement<InternalResizablePanelProps>;
      return cloneElement(panelChild, { ...panelChild.props, _index: currentIndex });
    }
    return child;
  });

  return (
    <ResizableContext.Provider value={{ direction, sizes, onResize }}>
      <div
        className={cn(
          'flex h-full w-full',
          direction === 'vertical' && 'flex-col',
          className
        )}
      >
        {childrenWithProps}
      </div>
    </ResizableContext.Provider>
  );
}

export function ResizablePanel({ children, className, _index = 0 }: InternalResizablePanelProps) {
  const context = useContext(ResizableContext);
  if (!context) throw new Error('ResizablePanel must be used within ResizablePanelGroup');

  const { direction, sizes } = context;
  const size = sizes[_index] || 50;

  return (
    <div
      className={cn('overflow-hidden', className)}
      style={{
        [direction === 'horizontal' ? 'width' : 'height']: `${size}%`,
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

export function ResizableHandle({ className }: ResizableHandleProps) {
  const context = useContext(ResizableContext);
  if (!context) throw new Error('ResizableHandle must be used within ResizablePanelGroup');

  const { direction, onResize } = context;
  const isDragging = useRef(false);
  const startPos = useRef(0);
  const handleIndexRef = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startPos.current = direction === 'horizontal' ? e.clientX : e.clientY;

    const parent = (e.target as HTMLElement).parentElement;
    if (parent) {
      const children = Array.from(parent.children);
      const handleIndex = children.indexOf(e.target as HTMLElement);
      handleIndexRef.current = Math.floor(handleIndex / 2);
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.current) return;

    const currentPos = direction === 'horizontal' ? e.clientX : e.clientY;
    const delta = ((currentPos - startPos.current) / window.innerWidth) * 100;
    startPos.current = currentPos;

    onResize(handleIndexRef.current, delta);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      className={cn(
        'relative flex items-center justify-center bg-accent-2 transition-colors hover:bg-border',
        direction === 'horizontal' ? 'w-1.5 cursor-col-resize' : 'h-1.5 cursor-row-resize',
        className
      )}
    >
      <GripVertical className={cn(
        'w-3 h-3 text-muted-foreground',
        direction === 'vertical' && 'rotate-90'
      )} />
    </div>
  );
}
